import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import {
  X,
  Send,
  Bot,
  User,
  Sparkles,
  Trash2,
  Minimize2,
  Maximize2,
  Copy,
  Check,
  RefreshCw,
  ShieldCheck,
  ChevronDown,
  ShieldAlert,
  FileSpreadsheet,
  CheckCircle2,
  TrendingUp,
  Stethoscope,
  Download,
  ThumbsUp,
  ThumbsDown,
  CornerDownLeft,
  AlertCircle,
  Loader2,
  Lock,
} from 'lucide-react';
import { apiService } from '../services/apiService';
import { ChatMessage } from '../types/chat';
import { useAuth } from '../contexts/AuthContext';
import { useSystemLoading } from '../contexts/LoadingContext';
import { useUpload } from '../contexts/UploadContext';

interface QuickPrompt {
  id: string;
  icon: React.ReactNode;
  label: string;
  query: string;
}

const getQuickPromptIcon = (iconName: string) => {
  switch (iconName) {
    case 'Stethoscope':
      return <Stethoscope className="w-3.5 h-3.5 text-blue-600" />;
    case 'FileSpreadsheet':
      return <FileSpreadsheet className="w-3.5 h-3.5 text-indigo-600" />;
    case 'TrendingUp':
      return <TrendingUp className="w-3.5 h-3.5 text-amber-600" />;
    case 'CheckCircle2':
      return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />;
    case 'ShieldAlert':
    default:
      return <ShieldAlert className="w-3.5 h-3.5 text-rose-600" />;
  }
};

const DEFAULT_QUICK_PROMPTS: QuickPrompt[] = [
  {
    id: 'physician-report',
    icon: <Stethoscope className="w-3.5 h-3.5 text-blue-600" />,
    label: 'أداء أعلى الأطباء مخاطرة (Physician Report)',
    query: 'ما هو تقرير أداء أعلى طبيب من حيث حالات الاحتيال والمحاور الأربعة؟',
  },
  {
    id: 'inpatient-outpatient',
    icon: <FileSpreadsheet className="w-3.5 h-3.5 text-indigo-600" />,
    label: 'تحليل In-Patient / Out-Patient',
    query: 'ما هو تحليل الحالات الداخلية والخارجية In-Patient vs Out-Patient؟',
  },
  {
    id: 'change-of-amount',
    icon: <TrendingUp className="w-3.5 h-3.5 text-amber-600" />,
    label: 'فروقات المبالغ (Change of Amount)',
    query: 'ما هو تحليل تغيير وفروقات المبالغ Change of Amount بين Gross و Net؟',
  },
  {
    id: 'fraud-overview',
    icon: <ShieldAlert className="w-3.5 h-3.5 text-rose-600" />,
    label: 'كشف الاحتيال والمطالبات (Fraud Detection)',
    query: 'كم إجمالي المطالبات وحالات الاحتيال في لوحة التحكم؟',
  },
  {
    id: 'fraud-engine',
    icon: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />,
    label: 'محرك كشف الاحتيال (3-Layer Engine)',
    query: 'كيف يعمل محرك كشف الاحتيال ثلاثي الطبقات 3-Layer Fraud Engine؟',
  },
  {
    id: 'facilities-summary',
    icon: <ShieldAlert className="w-3.5 h-3.5 text-purple-600" />,
    label: 'ملخص المنشآت والمستشفيات المسجلة',
    query: 'أعرض لي مقارنة شاملة بين المنشآت الطبية وأطباء كل منشأة',
  },
];

const ISSUE_TAGS = [
  'Inaccurate claims data',
  'Too long / Unclear',
  'Missing key details',
  'Irrelevant / Off-topic',
  'Wrong dashboard section',
  'Outdated policy rule',
];

const formatAssistantMessage = (content: string): string => {
  if (!content) return '';
  // Convert <br> or <br/> right before numbered list items (e.g. <br>2. ) or bullets into standard double newlines
  return content.replace(/<br\s*\/?>\s*(?=\d+\.|\*|-|•)/gi, '\n\n');
};

export const Chatbot: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { isSystemLoading } = useSystemLoading();
  const { uploading, currentStage } = useUpload();
  const isBusy = isSystemLoading || uploading;
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    const saved = localStorage.getItem('fraudguard_chat_history');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch (e) {
        // fallback
      }
    }
    return [
      {
        id: 'welcome',
        role: 'assistant',
        content:
          'Hello! How can I assist you with your claims data, fraud detection rules, or dashboard analytics today?',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      },
    ];
  });
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [feedbackState, setFeedbackState] = useState<Record<string, 'up' | 'down'>>({});
  const [dislikeModalId, setDislikeModalId] = useState<string | null>(null);
  const [dislikeReasons, setDislikeReasons] = useState<Record<string, string[]>>({});
  const [dislikeComment, setDislikeComment] = useState<Record<string, string>>({});
  const [feedbackSuccessId, setFeedbackSuccessId] = useState<string | null>(null);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const [quickPrompts, setQuickPrompts] = useState<QuickPrompt[]>(DEFAULT_QUICK_PROMPTS);

  useEffect(() => {
    let isMounted = true;
    apiService
      .getQuickPrompts()
      .then((res) => {
        if (isMounted && res?.prompts && res.prompts.length > 0) {
          const mapped = res.prompts.map((p) => ({
            id: p.id,
            icon: getQuickPromptIcon(p.icon),
            label: p.label,
            query: p.query,
          }));
          setQuickPrompts(mapped);
        }
      })
      .catch(() => {
        // Fall back to DEFAULT_QUICK_PROMPTS
      });
    return () => {
      isMounted = false;
    };
  }, [uploading]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = useCallback((smooth = true) => {
    messagesEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    const isFarFromBottom = scrollHeight - scrollTop - clientHeight > 140;
    setShowScrollBottom(isFarFromBottom);
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom(false);
      setUnreadCount(0);
      setTimeout(() => textareaRef.current?.focus(), 150);
    }
  }, [isOpen, scrollToBottom]);

  useEffect(() => {
    if (isOpen && !showScrollBottom) {
      scrollToBottom(true);
    }
  }, [messages, isOpen, showScrollBottom, scrollToBottom]);

  useEffect(() => {
    try {
      localStorage.setItem('fraudguard_chat_history', JSON.stringify(messages));
    } catch (e) {
      // ignore
    }
  }, [messages]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'j') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      } else if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleSendMessage = async (textToSend?: string) => {
    const text = (textToSend || input).trim();
    if (!text || isLoading || isBusy) return;

    const userMessage: ChatMessage = {
      id: `usr-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    setIsLoading(true);

    try {
      const payload = newMessages.map((m) => ({
        role: m.role,
        content: m.content,
      }));

      const res = await apiService.sendChatMessage(payload);

      const assistantMessage: ChatMessage = {
        id: `asst-${Date.now()}`,
        role: 'assistant',
        content: res.reply,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };

      setMessages((prev) => [...prev, assistantMessage]);
      if (!isOpen) {
        setUnreadCount((c) => c + 1);
      }
    } catch (err: any) {
      const errorMessage: ChatMessage = {
        id: `err-${Date.now()}`,
        role: 'assistant',
        content: `⚠️ **Connection Error**: ${err.message || 'Unable to reach the claims assistant. Please try again.'}`,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isError: true,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTextareaInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isBusy && !isLoading) {
        handleSendMessage();
      }
    }
  };

  const handleClearChat = () => {
    const resetMessage: ChatMessage = {
      id: `welcome-${Date.now()}`,
      role: 'assistant',
      content:
        'Hello! How can I assist you with your claims data, fraud detection rules, or dashboard analytics today?',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };
    setMessages([resetMessage]);
    localStorage.removeItem('fraudguard_chat_history');
  };

  const copyToClipboard = (id: string, text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleExportChat = () => {
    const chatLog = messages
      .map((m) => `[${m.timestamp || ''}] ${m.role.toUpperCase()}:\n${m.content}\n`)
      .join('\n---\n\n');
    const blob = new Blob([chatLog], { type: 'text/markdown;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `claims_chat_${new Date().toISOString().slice(0, 10)}.md`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Feedback Actions
  const handleThumbsUp = async (msgKey: string) => {
    setFeedbackState((prev) => ({ ...prev, [msgKey]: 'up' }));
    if (dislikeModalId === msgKey) setDislikeModalId(null);
    try {
      await apiService.submitChatFeedback({
        message_id: msgKey,
        rating: 'up',
      });
      setFeedbackSuccessId(msgKey);
      setTimeout(() => setFeedbackSuccessId(null), 2500);
    } catch (e) {
      // ignore
    }
  };

  const handleThumbsDownClick = (msgKey: string) => {
    setFeedbackState((prev) => ({ ...prev, [msgKey]: 'down' }));
    setDislikeModalId((prev) => (prev === msgKey ? null : msgKey));
  };

  const toggleIssueTag = (msgKey: string, tag: string) => {
    setDislikeReasons((prev) => {
      const current = prev[msgKey] || [];
      const exists = current.includes(tag);
      return {
        ...prev,
        [msgKey]: exists ? current.filter((t) => t !== tag) : [...current, tag],
      };
    });
  };

  const handleSubmitDislikeFeedback = async (msgKey: string) => {
    setIsSubmittingFeedback(true);
    try {
      await apiService.submitChatFeedback({
        message_id: msgKey,
        rating: 'down',
        reasons: dislikeReasons[msgKey] || [],
        comment: dislikeComment[msgKey] || '',
      });
      setDislikeModalId(null);
      setFeedbackSuccessId(msgKey);
      setTimeout(() => setFeedbackSuccessId(null), 3000);
    } catch (e) {
      setDislikeModalId(null);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated || isSystemLoading) {
      setIsOpen(false);
    }
  }, [isAuthenticated, isSystemLoading]);

  if (!isAuthenticated || isSystemLoading) {
    return null;
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 font-sans select-none antialiased">
      {/* ── Floating Action Button (FAB) (Clean Light Theme) ─────────────────── */}
      {!isOpen && (
        <div className="relative group">
          <button
            onClick={() => !isBusy && setIsOpen(true)}
            disabled={isBusy}
            className={`flex items-center gap-3 px-5 py-3.5 rounded-full shadow-[0_10px_25px_-5px_rgba(37,99,235,0.4)] transition-all duration-300 transform border border-white/20 ${
              isBusy
                ? 'bg-slate-700/90 text-slate-300 cursor-not-allowed opacity-75'
                : 'bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 hover:from-blue-500 hover:to-indigo-500 text-white hover:shadow-blue-500/50 hover:-translate-y-0.5 active:scale-95'
            }`}
            aria-label={isBusy ? "Assistant locked while system is busy" : "Open Claims Assistant (Cmd+J)"}
          >
            <div className="relative flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
              <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500 border border-white"></span>
              </span>
            </div>
            <div className="flex flex-col text-left">
              <span className="font-bold text-sm tracking-wide text-white leading-tight">
                Claims Assistant
              </span>
              <span className="text-[10px] text-blue-100 font-normal">Press ⌘J</span>
            </div>
          </button>

          {unreadCount > 0 && (
            <span className="absolute -top-1.5 -left-1.5 bg-rose-500 text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full shadow-md border-2 border-white animate-bounce">
              {unreadCount}
            </span>
          )}
        </div>
      )}

      {/* ── Clean Light Chat Window ───────────────────────────────────────── */}
      {isOpen && (
        <div
          className={`flex flex-col bg-white/95 backdrop-blur-2xl border border-slate-200 shadow-[0_20px_60px_-15px_rgba(15,23,42,0.18)] rounded-3xl overflow-hidden transition-all duration-300 ${
            isExpanded
              ? 'fixed inset-4 sm:inset-8 z-50'
              : 'w-[94vw] sm:w-[530px] h-[680px] max-h-[88vh]'
          }`}
        >
          {/* Top Header */}
          <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-slate-50 via-white to-blue-50/40 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <div className="relative flex items-center justify-center w-10 h-10 rounded-2xl bg-gradient-to-tr from-blue-600 to-indigo-600 shadow-sm border border-blue-400/30">
                <Bot className="w-5 h-5 text-white" />
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 border-2 border-white rounded-full" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-bold text-slate-900 tracking-tight">Claims Assistant</h3>
                  <span className="px-2 py-0.5 text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Online
                  </span>
                </div>
                <p className="text-[11px] text-slate-500 mt-0.5 font-normal">
                  Live claims auditing & intelligence
                </p>
              </div>
            </div>

            {/* Header Controls */}
            <div className="flex items-center gap-1 text-slate-500">
              <button
                onClick={handleExportChat}
                title="Export chat transcript"
                className="p-2 hover:text-blue-600 hover:bg-slate-100 rounded-xl transition-all"
                aria-label="Export chat"
              >
                <Download className="w-4 h-4" />
              </button>
              <button
                onClick={handleClearChat}
                title="Clear conversation"
                className="p-2 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all"
                aria-label="Clear chat"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                title={isExpanded ? 'Collapse view' : 'Expand workspace'}
                className="p-2 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-all hidden sm:block"
                aria-label="Toggle size"
              >
                {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                title="Close assistant (Esc)"
                className="p-2 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-all"
                aria-label="Close chat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages Container */}
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4 text-xs sm:text-sm custom-scrollbar bg-slate-50/70 select-text"
          >
            {messages.map((msg, index) => {
              const isUser = msg.role === 'user';
              const msgKey = msg.id || String(index);
              const isDislikeOpen = dislikeModalId === msgKey;
              const isSuccess = feedbackSuccessId === msgKey;

              return (
                <div
                  key={msgKey}
                  className={`flex gap-3 ${isUser ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-2 duration-200`}
                >
                  {!isUser && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600 shadow-xs mt-0.5">
                      <Bot className="w-4 h-4" />
                    </div>
                  )}

                  <div className={`relative group max-w-[92%] sm:max-w-[85%] ${isUser ? 'items-end' : 'items-start'}`}>
                    <div
                      className={`p-4 rounded-2xl text-xs sm:text-sm leading-relaxed transition-all ${
                        isUser
                          ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-br-sm shadow-sm'
                          : msg.isError
                          ? 'bg-rose-50 text-rose-900 border border-rose-200 rounded-bl-sm shadow-xs'
                          : 'bg-white text-slate-800 border border-slate-200 rounded-bl-sm shadow-sm'
                      }`}
                    >
                      {isUser ? (
                        <p className="whitespace-pre-wrap font-normal leading-relaxed" dir="auto">{msg.content}</p>
                      ) : (
                        <div className="prose prose-slate prose-sm max-w-none space-y-2 overflow-x-auto text-slate-800" dir="auto">
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            rehypePlugins={[rehypeRaw]}
                            components={{
                              table: ({ children }) => (
                                <div className="my-3 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-xs">
                                  <table className="w-full text-left text-xs border-collapse">
                                    {children}
                                  </table>
                                </div>
                              ),
                              thead: ({ children }) => (
                                <thead className="bg-slate-100 text-slate-900 font-bold border-b border-slate-200 text-[11px] uppercase tracking-wider">
                                  {children}
                                </thead>
                              ),
                              tbody: ({ children }) => (
                                <tbody className="divide-y divide-slate-200">
                                  {children}
                                </tbody>
                              ),
                              tr: ({ children }) => (
                                <tr className="hover:bg-blue-50/40 transition-colors odd:bg-white even:bg-slate-50/60">
                                  {children}
                                </tr>
                              ),
                              th: ({ children }) => (
                                <th className="px-3.5 py-2.5 text-slate-800 font-semibold border-r border-slate-200 last:border-r-0">
                                  {children}
                                </th>
                              ),
                              td: ({ children }) => (
                                <td className="px-3.5 py-2.5 text-slate-700 leading-relaxed border-r border-slate-200 last:border-r-0 align-top">
                                  {children}
                                </td>
                              ),
                              blockquote: ({ children }) => (
                                <div className="my-2.5 border-l-4 border-blue-500 bg-blue-50 px-3.5 py-2 rounded-r-xl text-blue-900 text-xs shadow-xs">
                                  {children}
                                </div>
                              ),
                              h1: ({ children }) => (
                                <h1 className="text-base font-bold text-slate-900 mt-3 mb-1 border-b border-slate-200 pb-1">
                                  {children}
                                </h1>
                              ),
                              h2: ({ children }) => (
                                <h2 className="text-sm font-bold text-slate-900 mt-2.5 mb-1">
                                  {children}
                                </h2>
                              ),
                              h3: ({ children }) => (
                                <h3 className="text-xs font-semibold text-blue-900 mt-2 mb-0.5">
                                  {children}
                                </h3>
                              ),
                              ul: ({ children }) => (
                                <ul className="my-1.5 pl-4 space-y-1 list-disc text-slate-700 marker:text-blue-600">
                                  {children}
                                </ul>
                              ),
                              ol: ({ children }) => (
                                <ol className="my-1.5 pl-4 space-y-1 list-decimal text-slate-700 marker:text-blue-600">
                                  {children}
                                </ol>
                              ),
                              li: ({ children }) => (
                                <li className="leading-relaxed pl-0.5 text-slate-700">{children}</li>
                              ),
                              strong: ({ children }) => (
                                <strong className="font-semibold text-slate-900">{children}</strong>
                              ),
                              code: ({ children }) => (
                                <code className="px-1.5 py-0.5 bg-slate-100 text-blue-900 font-mono text-[11px] rounded border border-slate-200">
                                  {children}
                                </code>
                              ),
                              hr: () => <hr className="my-2.5 border-slate-200" />,
                            }}
                          >
                            {formatAssistantMessage(msg.content)}
                          </ReactMarkdown>
                        </div>
                      )}
                    </div>

                    {/* Metadata, Copy & Feedback */}
                    <div className={`flex items-center gap-2 mt-1.5 text-[10px] text-slate-400 px-1 ${isUser ? 'justify-end' : 'justify-start'}`}>
                      <span>{msg.timestamp}</span>

                      {!isUser && !msg.isError && (
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => copyToClipboard(msgKey, msg.content)}
                            className="p-1 hover:text-blue-600 rounded transition-colors"
                            title="Copy response"
                            aria-label="Copy text"
                          >
                            {copiedId === msgKey ? (
                              <Check className="w-3 h-3 text-emerald-600" />
                            ) : (
                              <Copy className="w-3 h-3 text-slate-500 hover:text-slate-800" />
                            )}
                          </button>

                          <button
                            onClick={() => handleThumbsUp(msgKey)}
                            className={`p-1 rounded transition-colors ${feedbackState[msgKey] === 'up' ? 'text-emerald-600 font-bold' : 'text-slate-400 hover:text-slate-600'}`}
                            title="Helpful response"
                            aria-label="Thumbs up"
                          >
                            <ThumbsUp className="w-3 h-3" />
                          </button>

                          <button
                            onClick={() => handleThumbsDownClick(msgKey)}
                            className={`p-1 rounded transition-colors ${feedbackState[msgKey] === 'down' ? 'text-rose-600 font-bold' : 'text-slate-400 hover:text-slate-600'}`}
                            title="Report an issue with this answer"
                            aria-label="Thumbs down"
                          >
                            <ThumbsDown className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* ── Success Toast Badge ─────────────────────────────── */}
                    {isSuccess && (
                      <div className="mt-2 flex items-center gap-1.5 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-1.5 animate-in fade-in duration-200">
                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                        <span>Thank you! Your feedback helps improve the assistant.</span>
                      </div>
                    )}

                    {/* ── Inline Issue Feedback Card for Disliked Reply ─── */}
                    {isDislikeOpen && (
                      <div className="mt-2.5 p-3.5 bg-white border border-slate-250 rounded-2xl shadow-md animate-in fade-in slide-in-from-top-1 duration-200 text-slate-800">
                        <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                          <div className="flex items-center gap-1.5 text-xs font-bold text-slate-900">
                            <AlertCircle className="w-4 h-4 text-amber-500" />
                            <span>What went wrong with this response?</span>
                          </div>
                          <button
                            onClick={() => setDislikeModalId(null)}
                            className="p-1 text-slate-400 hover:text-slate-700 rounded-lg"
                            title="Cancel"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>

                        {/* Issue Tag Pills */}
                        <div className="mt-2.5">
                          <label className="text-[11px] font-semibold text-slate-600 block mb-1.5">
                            Select issues that apply:
                          </label>
                          <div className="flex flex-wrap gap-1.5">
                            {ISSUE_TAGS.map((tag) => {
                              const isSelected = (dislikeReasons[msgKey] || []).includes(tag);
                              return (
                                <button
                                  key={tag}
                                  type="button"
                                  onClick={() => toggleIssueTag(msgKey, tag)}
                                  className={`text-[11px] px-2.5 py-1 rounded-lg border transition-all flex items-center gap-1 ${
                                    isSelected
                                      ? 'bg-blue-50 border-blue-400 text-blue-800 font-semibold shadow-xs'
                                      : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                                  }`}
                                >
                                  {isSelected && <Check className="w-3 h-3 text-blue-600" />}
                                  <span>{tag}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Explanation Textarea */}
                        <div className="mt-3">
                          <label className="text-[11px] font-semibold text-slate-600 block mb-1">
                            Additional details (optional):
                          </label>
                          <textarea
                            value={dislikeComment[msgKey] || ''}
                            onChange={(e) =>
                              setDislikeComment((prev) => ({ ...prev, [msgKey]: e.target.value }))
                            }
                            placeholder="Tell us what information was incorrect or how this answer could be better..."
                            rows={2}
                            className="w-full text-xs p-2.5 rounded-xl border border-slate-250 bg-slate-50 text-slate-800 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none"
                          />
                        </div>

                        {/* Actions */}
                        <div className="mt-3 flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => setDislikeModalId(null)}
                            className="text-xs px-3 py-1.5 text-slate-500 hover:text-slate-800 font-medium rounded-lg"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSubmitDislikeFeedback(msgKey)}
                            disabled={isSubmittingFeedback}
                            className="text-xs px-3.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl shadow-xs transition-all flex items-center gap-1.5 disabled:opacity-50"
                          >
                            {isSubmittingFeedback ? (
                              <RefreshCw className="w-3 h-3 animate-spin text-white" />
                            ) : (
                              <Check className="w-3.5 h-3.5 text-white" />
                            )}
                            <span>Submit Feedback</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {isUser && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-xl bg-indigo-50 border border-indigo-200 flex items-center justify-center text-indigo-600 shadow-xs mt-0.5">
                      <User className="w-4 h-4" />
                    </div>
                  )}
                </div>
              );
            })}

            {/* Typing Indicator */}
            {isLoading && (
              <div className="flex items-center gap-3 text-slate-500 animate-in fade-in duration-150">
                <div className="w-8 h-8 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600 shadow-xs">
                  <Bot className="w-4 h-4 animate-spin" />
                </div>
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-1.5 shadow-sm">
                  <span className="w-2 h-2 rounded-full bg-blue-600 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 rounded-full bg-blue-600 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 rounded-full bg-blue-600 animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Floating "Scroll to Bottom" Button */}
          {showScrollBottom && (
            <button
              onClick={() => scrollToBottom(true)}
              className="absolute bottom-28 right-6 p-2 bg-white hover:bg-slate-50 text-blue-600 rounded-full shadow-lg border border-slate-200 transition-all transform hover:scale-110"
              aria-label="Scroll to bottom"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          )}

          {/* System Busy Notice */}
          {isBusy && (
            <div className="px-4 py-2.5 bg-amber-500/10 border-t border-amber-500/20 flex items-center justify-between text-xs text-amber-800 font-medium animate-fade-in">
              <span className="flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-600 shrink-0" />
                <span>Claims Engine is processing files ({currentStage || 'Analyzing...'}). Chatbot is temporarily locked.</span>
              </span>
              <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-md bg-amber-200/80 text-amber-900 flex items-center gap-1">
                <Lock className="w-3 h-3" /> Locked
              </span>
            </div>
          )}

          {/* Quick Prompts Carousel */}
          <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-200">
            <div className="flex items-center gap-1 text-[11px] text-slate-600 font-medium mb-1.5">
              <Sparkles className="w-3.5 h-3.5 text-blue-600" />
              <span>Suggested Inquiries:</span>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 custom-scrollbar scroll-smooth">
              {quickPrompts.map((prompt) => (
                <button
                  key={prompt.id}
                  onClick={() => handleSendMessage(prompt.query)}
                  disabled={isLoading || isBusy}
                  className="flex items-center gap-1.5 flex-shrink-0 text-[11px] px-3 py-1.5 bg-white hover:bg-blue-50/80 text-slate-700 hover:text-blue-700 border border-slate-200 hover:border-blue-300 rounded-xl transition-all duration-200 disabled:opacity-50 shadow-xs"
                >
                  {prompt.icon}
                  <span>{prompt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Input Dock */}
          <div className="p-3.5 bg-white border-t border-slate-200">
            <div className="relative flex items-center bg-slate-50 rounded-2xl border border-slate-300 focus-within:bg-white focus-within:border-blue-600 focus-within:ring-2 focus-within:ring-blue-500/20 transition-all">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={handleTextareaInput}
                onKeyDown={handleKeyDown}
                placeholder={
                  isBusy
                    ? 'Claims Engine is processing... Chatbot is locked until complete.'
                    : 'Ask any question about claims, fraud rules, or metrics...'
                }
                rows={1}
                disabled={isLoading || isBusy}
                className="w-full resize-none pl-4 pr-12 py-3 bg-transparent text-slate-900 placeholder-slate-400 text-xs sm:text-sm focus:outline-none disabled:opacity-50 max-h-32 custom-scrollbar disabled:cursor-not-allowed"
              />
              <button
                onClick={() => handleSendMessage()}
                disabled={!input.trim() || isLoading || isBusy}
                className="absolute right-2 p-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl transition-all shadow-sm flex items-center justify-center active:scale-95"
                title={isBusy ? 'Assistant is locked while processing' : 'Send message (Enter)'}
                aria-label="Send message"
              >
                {isLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin text-white" />
                ) : (
                  <Send className="w-4 h-4 text-white" />
                )}
              </button>
            </div>

            {/* Bottom Status bar */}
            <div className="flex items-center justify-between mt-2 text-[10px] text-slate-500 px-1 font-medium">
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-blue-600" />
                Claims Intelligence Support
              </span>
              <span className="flex items-center gap-1 text-slate-400">
                <CornerDownLeft className="w-3 h-3 text-slate-400" /> Send
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chatbot;
